import { useState, useRef, useEffect, ReactElement } from 'react';
import { useSelector } from 'react-redux';

import { PlaybackTypes } from 'models/kitchenSink';
import {
  appendHLSEncryptionConfig,
  appendFullDRMConfig,
  ResourceConfig,
} from 'support/player/resourceHelper';
import { fetchJWTToken } from 'support/api/VideoApi';
import { VideoCtvRedux, KitcenSinkRowItemDataItem, PregameVideo, RelatedItem } from 'store-types';

// Video type can be full video, pregame video, or null
type DRMVideoType = KitcenSinkRowItemDataItem | PregameVideo | RelatedItem | null | undefined;

interface DRMProviderProps {
  resource?: ResourceConfig | null;
  video: DRMVideoType;
  onError?: (error: { type: string }) => void;
  render: (props: { resource?: ResourceConfig | null }) => ReactElement | null;
}

interface ContextRef {
  video: DRMVideoType;
  userPackage: string | undefined;
  userId: number | undefined;
  tokenUrl: string;
  onError: (error: { type: string }) => void;
  widevineUrl: string;
  playreadyUrl: string;
}

const DRMProvider = ({
  resource = null,
  video,
  onError = () => {},
  render,
}: DRMProviderProps): ReactElement | null => {
  const [res, setRes] = useState<ResourceConfig | undefined>();
  const userPackage = useSelector(
    (state: VideoCtvRedux) => state.auth.paramountPlus.user.data?.userPackage,
  );
  const userId = useSelector(
    (state: VideoCtvRedux) => state.auth.paramountPlus.user.data?.userId,
  );
  const tokenUrl = useSelector((state: VideoCtvRedux) => {
    if (video?.playback?.type === PlaybackTypes.HLS_ENCRYPTION) {
      return state.kitchenSink.data.playback?.[PlaybackTypes.HLS_ENCRYPTION]
        .tokenUrl;
    }
    if (video?.playback?.type === PlaybackTypes.FULL_DRM) {
      return state.kitchenSink.data.playback[PlaybackTypes.FULL_DRM].tokenUrl;
    }
    if (video?.playback?.type === PlaybackTypes.STATION_OPT_IN) {
      return state.kitchenSink.data.playback?.[PlaybackTypes.STATION_OPT_IN]
        ?.tokenUrl;
    }
    return '';
  });
  const widevineUrl = useSelector(
    (state: VideoCtvRedux) => state.kitchenSink.data.playback?.[PlaybackTypes.FULL_DRM]?.widevine,
  );
  const playreadyUrl = useSelector(
    (state: VideoCtvRedux) => state.kitchenSink.data.playback?.[PlaybackTypes.FULL_DRM]?.playready,
  );

  const context = useRef<ContextRef>();
  useEffect(() => {
    context.current = {
      video,
      userPackage,
      userId,
      tokenUrl,
      onError,
      widevineUrl,
      playreadyUrl,
    };
  });

  useEffect(() => {
    const {
      video: vid,
      userPackage: packageCode,
      userId: id,
      tokenUrl: url,
      onError: onErr,
      widevineUrl: wvUrl,
      playreadyUrl: prUrl,
    } = context.current as ContextRef;
    if (
      resource &&
      vid &&
      (vid?.playback?.type === PlaybackTypes.HLS_ENCRYPTION ||
        vid?.playback?.type === PlaybackTypes.FULL_DRM ||
          vid?.playback?.type === PlaybackTypes.STATION_OPT_IN)
    ) {
      fetchJWTToken(url, id, packageCode)
        .then(token => {
          if (
            vid?.playback?.type === PlaybackTypes.HLS_ENCRYPTION ||
            vid?.playback?.type === PlaybackTypes.STATION_OPT_IN
          ) {
            setRes(appendHLSEncryptionConfig(typeof resource === 'object' ? { ...resource } : resource, token));
          } else if (vid?.playback?.type === PlaybackTypes.FULL_DRM) {
            setRes(
              appendFullDRMConfig(
                resource,
                token,
                vid.viacomContentId as string,
                wvUrl,
                prUrl,
              ),
            );
          }
        })
        .catch(() => {
          // do nothing
          onErr({ type: 'DRM' });
        });
    } else {
      setRes(resource as ResourceConfig | undefined);
    }
  }, [resource]);

  return render({ resource: res });
};

export default DRMProvider;
